import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Images, Package } from 'lucide-react'
import { toast } from 'sonner'
import type {
    CreateProductPayload,
    UpdateProductPayload,
} from '@/lib/api/product-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { FormSection } from '@/components/forms/form-section'
import { ProductForm } from '@/components/products/product-form'
import { NewProductImagePicker } from '@/components/products/new-product-image-picker'
import {
    useCreateProduct,
    useUploadProductImage,
} from '@/hooks/use-product-query'
import { ApiError } from '@/lib/api/client'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { m } from '@/lib/paraglide/messages'
import { productFieldError } from '@/lib/product-form-errors'
import { userErrorToast } from '@/lib/user-error-toast'

export const Route = createFileRoute('/_app/_authed/_sidebar/products/new')({
    component: NewProductPage,
})

function NewProductPage() {
    const navigate = useNavigate()
    const createProduct = useCreateProduct()
    const uploadImage = useUploadProductImage()
    // Ảnh chọn tạm — upload sau khi tạo sản phẩm (cần product_id). Một ảnh.
    const [image, setImage] = useState<File | null>(null)
    const [busy, setBusy] = useState(false)

    const handleSubmit = async (
        data: CreateProductPayload | UpdateProductPayload,
    ) => {
        setBusy(true)
        try {
            const res = await createProduct.mutateAsync(
                data as CreateProductPayload,
            )
            const productId = res.data?.id
            // Lỗi upload không huỷ việc tạo SP — báo toast rồi vẫn điều hướng
            // vào chi tiết (ở đó thay ảnh / thiết kế ảnh được).
            if (productId && image) {
                try {
                    await uploadImage.mutateAsync({ productId, file: image })
                } catch (err) {
                    // Cùng cách báo với ô thay ảnh ở màn chi tiết: mã lỗi đã
                    // biết ra câu cụ thể, còn lại kèm lý do.
                    toast.error(
                        (err instanceof ApiError &&
                            resolveApiErrorMessage(err.code, err.params)) ||
                            m.product_image_upload_error({
                                msg: err instanceof Error ? err.message : '',
                            }),
                    )
                }
            }
            toast.success(m.toast_product_create_success())
            if (productId) {
                void navigate({
                    to: '/products/$productId',
                    params: { productId },
                    replace: true,
                })
            } else {
                void navigate({ to: '/products' })
            }
        } catch (err) {
            // Lỗi gắn với một ô (trùng mã vạch / mã hàng) do form hiện ngay dưới
            // ô, kèm popup khi cần; lỗi khác ra câu cụ thể theo mã lỗi. Ném lại
            // để form xử lý phần của nó.
            if (!productFieldError(err)) {
                const { title, description } = userErrorToast(
                    err,
                    m.toast_product_create_error(),
                )
                toast.error(title, { description })
            }
            throw err
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className="space-y-4 page-pad md:space-y-6">
            <div className="flex items-center gap-3">
                <PageBackButton onBack={() => navigate({ to: '/products' })} />
                <h1 className="text-xl font-semibold">{m.product_add_new()}</h1>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(320px,380px)_minmax(0,1fr)] lg:items-start">
                <div className="min-w-0 space-y-5 lg:col-start-1 lg:row-start-1">
                    <FormSection
                        icon={<Images />}
                        title={m.product_images_section_title()}
                    >
                        <NewProductImagePicker
                            file={image}
                            onChange={setImage}
                            disabled={busy}
                        />
                    </FormSection>
                </div>

                <div className="min-w-0 space-y-5 lg:col-start-2 lg:row-start-1">
                    <FormSection
                        icon={<Package />}
                        title={m.product_info_section_title()}
                    >
                        <ProductForm
                            onSubmit={handleSubmit}
                            onCancel={() => navigate({ to: '/products' })}
                            isLoading={busy}
                        />
                    </FormSection>
                </div>
            </div>
        </div>
    )
}
